import { LoggedUserInfo } from 'common/support-chat/utils';
import { SupportChatConfig } from 'common/support-chat/getSupportChatConfig';
import { withSentryScope } from 'helpers/withSentryScope';
import { ChatSourceType } from 'src/tracking-container/events/chat';
import * as Sentry from '@sentry/browser';
import { tokenProviders } from 'common/support-chat/tokens';
import { getTrackingByEventName } from 'src/tracking-container/TrackingEvents';
import { OneChatApp } from './index.d';

export const SUPPORT_CHAT_WIDGET_ID = 'support-chat-widget';
export const SUPPORT_CHAT_GLOBAL = 'OneChatApp';

let chatWidget: OneChatApp;
const trackEvent = getTrackingByEventName('chat_opened');

export function onSupportChatOpen(
  userInfo: LoggedUserInfo,
  conf: SupportChatConfig,
  el: HTMLElement,
) {
  return withSentryScope(async ({ source }: { source: ChatSourceType }) => {
    trackEvent({ service: 'Salesforce', source });
    const handleError = withHandleError(source, userInfo);
    Sentry.addBreadcrumb({
      category: 'Salesforce chat',
      message: 'Custom chat initialising',
      data: { source, userInfo },
    });
    try {
      await loadScript(conf.bootstrapJsSrc);
    } catch (err) {
      handleError(
        err,
        'Error loading custom chat script',
        true,
        'Failed to load the chat, please try again later.',
      );
      return;
    }
    try {
      if (!window[SUPPORT_CHAT_GLOBAL]) {
        throw new Error('Script loaded but chat object not found in window scope.');
      }
      conf.init.rootElementId = el.id;
      if (!chatWidget) {
        chatWidget = window.OneChatApp({
          ...conf.init,
          userInfo: {
            ...userInfo,
            source: 'Web',
          },
          getAccessToken: tokenProviders.getAccessToken,
          getProfileAccessToken: tokenProviders.getProfileAccessToken,
        });
        await chatWidget.init();
      }
      chatWidget.open();
    } catch (err) {
      handleError(
        err,
        'Error initializing the chat',
        true,
        'Failed to launch the chat, please try again later.',
      );
    }
  });
}

const loadScript = (src: string): Promise<void> => {
  if (window && window[SUPPORT_CHAT_GLOBAL] !== undefined) {
    console.log('Support-chat widget already loaded');
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', (ev: ErrorEvent) => {
      reject(ev);
    });
    document.head.appendChild(script);
  });
};

const withHandleError = (source: ChatSourceType, additionalData: object) => (
  error: unknown,
  message: string,
  showNotification: boolean,
  userMessage?: string,
) => {
  message = `[custom-support-chat] ${message}`;
  console.error(message);
  Sentry.captureException({
    message: message,
    data: { error, additionalData, source },
  });
  if (showNotification) {
    Wahanda.ToastNotifications.sendMessage(userMessage, {
      variant: 'danger',
      timeout: 2500,
    });
  }
};
